/**
 * storageService.ts
 * 
 * Service for managing files in Firebase Storage
 */
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { app } from '../firebase';
import { logger } from '../utils/logger';

// Initialize Firebase Storage
const storage = getStorage(app);

/**
 * Upload a file to Firebase Storage with proper user access control
 * 
 * @param file File to upload
 * @param path Path in storage to upload to (e.g., 'profilePhotos/userId')
 * @param metadata Optional metadata for the file
 * @returns Download URL of the uploaded file
 */
export const uploadFile = async (
  file: File,
  path: string,
  metadata: Record<string, string> = {}
): Promise<string> => {
  try {
    // Create a unique file name with timestamp to avoid collisions
    const fileName = `${file.name.split('.')[0]}-${Date.now()}.${file.name.split('.').pop()}`;
    const filePath = `${path}/${fileName}`;
    const fileRef = ref(storage, filePath);

    logger.info('🚀 Uploading file to Firebase Storage', { path: filePath });
    
    // Upload the file with metadata
    await uploadBytes(fileRef, file, { customMetadata: metadata });
    
    // Get download URL
    const downloadURL = await getDownloadURL(fileRef);
    
    logger.success('✅ File uploaded successfully', { url: downloadURL });
    return downloadURL;
  } catch (error) {
    logger.error('❌ Error uploading file to Firebase Storage', error);
    throw error;
  }
};

/**
 * Upload a user profile photo to Firebase Storage
 * 
 * @param userId User ID
 * @param file Photo file to upload
 * @returns Download URL of the uploaded photo
 */
export const uploadProfilePhoto = async (userId: string, file: File): Promise<string> => {
  const path = `profilePhotos/${userId}`;
  return uploadFile(file, path, { ownerId: userId, type: 'profile' });
};

/**
 * Upload user avatar to Firebase Storage
 * 
 * @param userId User ID
 * @param file Avatar file to upload (or blob converted to file)
 * @returns Download URL of the uploaded avatar
 */
export const uploadAvatar = async (userId: string, file: File): Promise<string> => {
  const path = `profileAvatars/${userId}`;
  return uploadFile(file, path, { ownerId: userId, type: 'avatar' });
};

/**
 * Delete a file from Firebase Storage
 * 
 * @param url Full download URL of the file to delete
 */
export const deleteFileByUrl = async (url: string): Promise<void> => {
  try {
    // Extract the storage path from the URL
    const fileRef = ref(storage, url);
    
    logger.info('🗑️ Deleting file from Firebase Storage', { url });
    await deleteObject(fileRef);
    
    logger.success('✅ File deleted successfully');
  } catch (error) {
    logger.error('❌ Error deleting file from Firebase Storage', error);
    throw error;
  }
};

/**
 * Determine if a string is a valid HTTP URL
 * 
 * @param str String to check
 * @returns True if the string is a valid URL
 */
export const isValidUrl = (str: string): boolean => {
  try {
    const url = new URL(str);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};
